import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../../assets/image/logo.svg";
import { Utensils, ChevronDown } from "lucide-react";
import pizza from "../../assets/image/pizza.jpg";
import aperitivo from "../../assets/image/aperitivo.jpg";
import tagliere from "../../assets/image/tagliere.jpg";
import FramedImage from "../../components/framed/FramedImage";
import Lottie from "lottie-react";
import beerBubbles from "../../assets/lottie/beer-bubbles.json";
import { useNavigate } from "react-router-dom";
import PartnerCarousel from "../../components/carousel/PartnerCarousel";
import aic from "../../assets/partner/aic.webp"
import CustomerReviews from "../../section/review/CustomerReviews";
import PizzaioliSection from "../../section/piazzaioli/PizzaioliSection";
import ImpastoSection from "../../section/impasto/ImpastoSection";
import ContactSection from "../../section/contact/ContactSection";
import Footer from "../../components/footer/Footer";

const Home = () => {
	const navigate = useNavigate();
	const menuButtonRef = useRef(null);
	const [isSticky, setIsSticky] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		setIsMobile(window.innerWidth < 640);
	}, []);

	useEffect(() => {
		const timeout = setTimeout(() => setShowVideo(true), 300);
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		const target = menuButtonRef.current;
		const observer = new IntersectionObserver(
			([entry]) => setIsSticky(!entry.isIntersecting),
			{ threshold: 0.1 }
		);
		if (target) observer.observe(target);
		return () => target && observer.unobserve(target);
	}, []);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				document.querySelectorAll("video").forEach((video) => {
					if (video.paused && video.readyState >= 2) {
						video.play().catch(() => { });
					}
				});
			}
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, []);

	return (
		<main className="bg-[#090D16] text-secondary font-sans w-full overflow-x-hidden selection:bg-primary/20">
			{/* Header Sticky Premium */}
			<header
				className={`fixed top-0 left-0 w-full z-50 py-4 px-6 flex items-center justify-center transition-all duration-500 border-b
                            ${isSticky ? "bg-[#0D121F]/90 backdrop-blur-md border-accent/40 shadow-xl opacity-100" : "opacity-0 pointer-events-none border-transparent"}`}
			>
				<AnimatePresence>
					{isSticky && (
						<motion.img
							src={logo}
							alt="Logo"
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							className="absolute left-6 h-10 object-contain cursor-pointer"
							onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
						/>
					)}
				</AnimatePresence>

				<button
					onClick={() => navigate("/menu")}
					className="flex items-center gap-2 border border-primary/60 text-primary font-bold px-6 py-2 rounded-full bg-primary/5 hover:bg-primary hover:text-black transition-all duration-300 shadow-[0_0_15px_rgba(233,69,69,0.1)] hover:shadow-[0_0_25px_rgba(233,69,69,0.3)] active:scale-95"
				>
					<Utensils className="w-4 h-4" />
					Scopri il Menù
				</button>
			</header>

			{/* Hero Section Immersiva */}
			<section className="relative w-full h-safe-screen flex flex-col items-center justify-center text-center overflow-hidden bg-black">
				<div className="absolute inset-0 bg-gradient-to-t from-[#090D16] via-transparent to-black/50 z-10 pointer-events-none" />

				{showVideo && (
					<video
						autoPlay
						muted
						loop
						preload="auto"
						playsInline
						draggable={false}
						className="absolute inset-0 w-full h-full object-cover opacity-45 transform scale-105 transition-transform duration-[10s]"
					>
						<source src={isMobile ? "/videos/preparazione_mobile.mp4" : "/videos/preparazione_desktop.mp4"} type="video/mp4" />
					</video>
				)}

				{/* Blocco Centrale Brand */}
				<div className="relative z-20 px-4 max-w-xl mx-auto flex flex-col items-center space-y-6">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: "easeOut" }}
					>
						<img src={logo} alt="Logo Il Balconcino" className="w-52 md:w-64 mx-auto drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]" draggable={false} />
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<button
							ref={menuButtonRef}
							onClick={() => navigate("/menu")}
							className="bg-primary text-black font-black tracking-wide px-8 py-3.5 rounded-full hover:scale-105 transition-all duration-300 flex items-center gap-2.5 shadow-[0_10px_30px_rgba(233,69,69,0.3)] active:scale-95"
						>
							<Utensils className="w-5 h-5 stroke-[2.5]" />
							SCOPRI IL MENÙ
						</button>
					</motion.div>
				</div>

				{/* Scroll Indicator */}
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 1.2 }}
					className="absolute z-20 text-white/70 hover:text-primary transition-colors duration-300"
					style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
				>
					<a href="#filosofia" className="text-xs font-bold tracking-widest uppercase flex flex-col items-center gap-2">
						<span>Scopri la nostra storia</span>
						<ChevronDown className="animate-bounce w-5 h-5 text-primary" />
					</a>
				</motion.div>
			</section>

			{/* Filosofia & Team */}
			<PizzaioliSection />

			{/* AIC Badge Section Card Premium */}
			<section className="px-4 my-16 max-w-4xl mx-auto">
				<div className="bg-[#121824] border border-accent/30 p-8 md:p-12 text-center rounded-3xl relative overflow-hidden group">
					<div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

					<motion.h2
						className="text-2xl md:text-3xl font-title text-primary mb-4 tracking-tight"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
					>
						Associati AIC - Associazione Italiana Celiachia
					</motion.h2>

					<motion.p
						className="text-muted text-base md:text-lg leading-relaxed mb-6"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						transition={{ delay: 0.2, duration: 0.6 }}
					>
						Garantiamo attenzione e sicurezza nella preparazione di piatti senza glutine, seguendo i protocolli approvati da AIC per offrire un’esperienza serena anche alle persone celiache.
					</motion.p>

					<motion.div whileHover={{ scale: 1.05 }} className="inline-block">
						<a href="https://www.celiachia.it/" target="_blank" rel="noreferrer" className="block p-4 rounded-2xl backdrop-blur-sm shadow-inner">
							<img src={aic} alt="Logo AIC" className="h-16 md:h-20 object-contain rounded-md mx-auto transition-all duration-300 filter grayscale group-hover:grayscale-0" />
						</a>
					</motion.div>
				</div>
			</section>

			{/* Impasti Laboratorio */}
			<ImpastoSection />

			{/* Servizi / Le Nostre Proposte */}
			<section className="space-y-16 py-24 px-6 max-w-5xl mx-auto">
				{[
					{
						title: "Pizze Gourmet",
						desc: "Dalle combinazioni classiche ed intramontabili alle creazioni d'autore contemporanee, stese a mano e impreziosite solo con ingredienti d'eccellenza locali.",
						image: pizza,
						label: "Gourmet" // 🚀 Aggiunto il badge per FramedImage
					},
					{
						title: "I Taglieri del Territorio",
						desc: "Una ricercata selezione di salumi artigianali e formaggi autoctoni pugliesi, perfetti da condividere al centro del tavolo all'inizio del vostro percorso.",
						image: tagliere,
						label: "Territorio" // 🚀 Aggiunto il badge per FramedImage
					},
					{
						title: "Antipasti Sfiziosi",
						desc: "Fritti dorati e croccanti nati dalla fusione tra la tradizione gastronomica locale e l'autentico street food internazionale.",
						image: aperitivo,
						label: "Sfiziosità" // 🚀 Aggiunto il badge per FramedImage
					}
				].map((item, idx) => (
					<motion.div
						key={idx}
						// 🚀 group permette a FramedImage di reagire anche se l'utente fa hover sul testo a fianco
						className={`flex flex-col lg:flex-row ${idx % 2 !== 0 ? "lg:flex-row-reverse" : ""} gap-8 lg:gap-16 items-center group`}
						initial={{ opacity: 0, y: 40 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: "-100px" }}
						transition={{ duration: 0.7, ease: "easeOut" }}
					>
						{/* Blocco Immagine */}
						<div className="w-full lg:w-1/2">
							<FramedImage
								src={item.image}
								alt={item.title}
								variant="label"
								label={item.label}
							/>
						</div>

						{/* Blocco Testo */}
						<div className="w-full lg:w-1/2 text-left space-y-3">
							<span className="text-primary font-bold text-xs tracking-widest uppercase block transition-transform duration-300 group-hover:translate-x-1">
								La Nostra Cucina
							</span>
							<h3 className="text-2xl font-title text-secondary tracking-tight transition-colors duration-300 group-hover:text-primary">
								{item.title}
							</h3>
							<p className="text-muted text-sm md:text-base leading-relaxed">
								{item.desc}
							</p>
						</div>
					</motion.div>
				))}
			</section>

			{/* Esperienza Birra Multi-Sensoriale */}
			<section className="relative w-full min-h-[85vh] flex items-center justify-center overflow-hidden bg-black">
				<Lottie animationData={beerBubbles} loop className="absolute bottom-0 w-full min-h-[85vh] opacity-35 pointer-events-none z-10 mix-blend-screen" />
				<div className="absolute inset-0 bg-gradient-to-t from-black via-primary/10 to-black z-10 pointer-events-none" />

				<video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-50 filter grayscale contrast-125">
					<source src="/videos/birra.mp4" type="video/mp4" />
				</video>

				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="relative z-20 px-4 w-full max-w-2xl"
				>
					<div className="bg-[#0D121F]/80 backdrop-blur-md border border-accent/40 rounded-3xl p-8 md:p-12 text-center shadow-2xl">
						{/* Bagliore soft interno alla card al passaggio del mouse */}
						<div className="absolute -inset-px bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none" />

						<span className="text-primary text-xs font-black tracking-widest uppercase block mb-2 transition-transform duration-300 group-hover:translate-y-[-1px]">
							Spillatura Perfetta
						</span>

						<h2 className="text-3xl md:text-4xl font-title text-secondary mb-4 tracking-tight">
							L'Arte della Birra
						</h2>

						{/* Divisore centrale coerente con le sezioni precedenti */}
						<div className="w-10 h-[2px] bg-primary/60 rounded-full mx-auto mb-6" />

						<p className="text-muted text-sm md:text-base leading-relaxed mb-8 max-w-md mx-auto">
							Temperatura controllata al millesimo, inclinazione geometrica del calice e una schiuma compatta a protezione dei profumi. Esaltiamo ogni singola sfumatura dei malti e dei luppoli per offrirti l'abbinamento ideale con le nostre pizze.
						</p>

						<button
							onClick={() => navigate("/menu", { state: { preselectedCategoryId: "c91890ae-e0e1-40f8-9c6a-8bf940b351fa" } })}
							className="px-8 py-3.5 rounded-full bg-primary text-black font-black tracking-wide transition-all duration-300 shadow-lg shadow-primary/10 hover:shadow-[0_0_30px_rgba(233,69,69,0.4)] hover:scale-105 active:scale-95 relative z-10"
						>
							SCOPRI LE NOSTRE BIRRE
						</button>
					</div>
				</motion.div>
			</section>

			<PartnerCarousel />
			<CustomerReviews />

			<ContactSection />

			<Footer />
		</main>
	);
};

export default Home;